import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Language options available in the system
export const LANGUAGE_OPTIONS = [
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'ro', label: 'Romanian' },
];

// Translation strings — extend as needed
export const translations = {
  'en-GB': {
    // Navigation
    dashboard: 'Dashboard',
    sites: 'Sites',
    assignments: 'Assignments',
    taskAnalytics: 'Task Analytics',
    photoGallery: 'Photo Gallery',
    // Nav labels (from roleAccess)
    Dashboard: 'Dashboard',
    Sites: 'Sites',
    Tasks: 'Tasks',
    Documents: 'Documents',
    Drawings: 'Drawings',
    'Users & Teams': 'Users & Teams',
    Reports: 'Reports',
    'Import Data': 'Import Data',
    Billing: 'Billing',
    Help: 'Help',
    Today: 'Today',
    'My Tasks': 'My Tasks',
    'Report Issue': 'Report Issue',
    Profile: 'Profile',
    Overview: 'Overview',
    // Role badges
    Admin: 'Admin',
    'Site Manager': 'Site Manager',
    Worker: 'Worker',
    Client: 'Client',
    // Demo banner
    viewingLiveDemo: 'You are viewing the live demo',
    backToWebsite: 'Back to website',
    // Common buttons
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    submit: 'Submit',
    export: 'Export',
    close: 'Close',
    next: 'Next',
    confirm: 'Confirm',
    search: 'Search',
    filter: 'Filter',
    continue: 'Continue',
    loading: 'Loading…',
    error: 'Error',
    success: 'Success',
    // Dashboard headings
    adminDashboard: 'Admin Dashboard',
    adminDashboardSubtitle: 'Manage sites, compliance, and team activities',
    siteManagerDashboard: 'Site Manager',
    quickActions: 'Quick Actions',
    sitesOverview: 'Sites Overview',
    // Form labels
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    date: 'Date',
    status: 'Status',
    notes: 'Notes',
    description: 'Description',
    site: 'Site',
    company: 'Company',
    role: 'Role',
    actions: 'Actions',
    created: 'Created',
    updated: 'Updated',
    assigned: 'Assigned',
    dueDate: 'Due Date',
    priority: 'Priority',
    type: 'Type',
    category: 'Category',
    // Notifications
    savedSuccess: 'Saved successfully',
    deletedSuccess: 'Deleted successfully',
    updatedSuccess: 'Updated successfully',
    errorOccurred: 'An error occurred',
    // Onboarding
    languagePreferences: 'Language Preferences',
    systemLanguage: 'System Language',
    languageDescription: 'Choose the language you would like to use throughout SiteProof.',
    // Settings
    profileSettings: 'Profile Settings',
    companySettings: 'Company Settings',
    helpSupport: 'Help & Support',
    signOut: 'Sign out',
    language: 'Language',
    languageSaved: 'Language preference saved',
    // Shared UI components
    back: 'Back',
    notifications: 'Notifications',
    markAllRead: 'Mark all read',
    noNotifications: 'No notifications',
    clearAllNotifications: 'Clear all notifications',
    // Compliance dashboard
    complianceOverview: 'Compliance Overview',
    complianceSubtitle: 'Real-time compliance status across all monitored areas',
    complianceSubtitleSiteManager: 'Real-time compliance status across all monitored areas for your assigned sites',
    refresh: 'Refresh',
    allComplianceClear: 'All compliance areas are clear',
    noOutstandingActions: 'No outstanding actions across all 8 compliance areas',
    clickCardToResolve: 'Click any card below to view and resolve the flagged items',
    complianceFooter: 'Data is read directly from live records. Click any card to navigate to the filtered module view.',
    // Report builder
    reportBuilder: 'Report Builder',
    reportBuilderSubtitle: 'Generate compliance reports, audit packs, and performance analytics',
    downloadCSV: 'Download CSV',
    downloadPDF: 'Download PDF',
    generating: 'Generating...',
    generateReport: 'Generate Report',
    reportBuilderTab: 'Report Builder',
    auditPackTab: 'Audit Pack',
    // Task planner
    taskPlanner: 'Task Planner',
    photoEvidence: 'Photo Evidence',
    noPhotoEvidence: 'No photo evidence uploaded',
    noPhotoEvidenceDesc: 'The worker did not attach any photos for this task',
    // Workers list
    usersTeams: 'Users & Teams',
    usersTeamsSubtitle: 'Manage all users and their roles',
    inviteTeamMembers: 'Invite Team Members',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    admins: 'Admins',
    siteManagers: 'Site Managers',
    workers: 'Workers',
    clients: 'Clients',
    allRoles: 'All Roles',
    active: 'Active',
    disabled: 'Disabled',
    activeFiltersApplied: 'Active filters applied',
    noWorkersMatch: 'No workers match your filters',
    noTeamMembersYet: 'No team members yet',
    noWorkersMatchDesc: 'Try adjusting your search or filters',
    noTeamMembersDesc: 'Invite your first team member to get started',
    nameHeader: 'Name',
    emailHeader: 'Email',
    roleHeader: 'Role',
    statusHeader: 'Status',
    joinedHeader: 'Joined',
    lastLoggedInHeader: 'Last Logged In',
    assignedSitesHeader: 'Assigned Sites',
    allSitesLabel: 'All sites',
    noSitesLabel: 'No sites',
    neverLoggedInLabel: 'Never',
    moreSitesSuffix: '+{count} more',
    actionsHeader: 'Actions',
    viewDetails: 'View Details',
    view: 'View',
    retry: 'Retry',
    // Sites list
    mySites: 'My Sites',
    sitesProgress: 'Progress',
    sitesTitle: 'Sites',
    sitesSubtitleManager: 'Sites you manage',
    sitesSubtitleClient: 'Your project progress',
    sitesSubtitleAdmin: 'Manage and monitor all construction sites',
    addNewSite: 'Add New Site',
    totalSites: 'Total Sites',
    activeSites: 'Active Sites',
    avgCompliance: 'Avg Compliance',
    needsAttention: 'Needs Attention',
    searchSitesPlaceholder: 'Search sites or clients...',
    allCompliance: 'All Compliance',
    compliant: 'Compliant',
    warning: 'Warning',
    sitesFound: 'sites found',
    siteFound: 'site found',
    noSitesMatch: 'No sites match your filters',
    noSitesYet: 'No sites yet',
    noSitesMatchDesc: 'Try adjusting your search or filter criteria.',
    noSitesYetDesc: 'Get started by creating your first construction site.',
    noSitesAssignedManager: 'No sites assigned to you yet',
    noSitesAssignedManagerDesc: 'Your administrator hasn\'t assigned you to any sites yet. Once they do, your sites will appear here. Contact your company administrator to get access.',
    archive: 'Archive',
    unarchive: 'Unarchive',
    documents: 'Documents',
    userMenuRole: 'Role',
    // Worker Certifications
    workerCertificationsTitle: 'Worker Certifications',
    workerCertificationsSubtitle: 'Track and manage worker qualifications and expiry dates',
    addCertification: 'Add Certification',
    certTotal: 'Total',
    certExpired: 'Expired',
    certExpiringSoon: 'Expiring Soon',
    certValid: 'Valid',
    certSearchPlaceholder: 'Search worker name, cert type, number...',
    certColWorker: 'Worker',
    certColCertType: 'Certificate Type',
    certColCertNumber: 'Cert Number',
    certColExpiryDate: 'Expiry Date',
    certColStatus: 'Status',
    certColDocument: 'Document',
    certNoneFound: 'No certifications found',
    certNoneFoundDesc: 'Add the first certification using the button above',
    certViewDocument: 'View Document',
    certShowingCount: 'Showing {shown} of {total} certifications',
    certModalSubtitle: 'Record a worker qualification',
    certSelectWorker: 'Select worker...',
    certSelectType: 'Select type...',
    certOptional: 'optional',
    certUploadLabel: 'Upload Certificate',
    certUploadHint: 'PDF or image, optional',
    certUploadClick: 'Click to upload PDF or image',
    certUploadMaxSize: 'Max 10MB',
    certSaveCertification: 'Save Certification',
    certEditTitle: 'Edit Certification',
    certReplaceFile: 'Replace Certificate File',
    certClickToReplace: 'Click to replace file',
    certSaveChanges: 'Save Changes',
    certExpiredBannerTitle: 'Expired Certification',
    certExpiringSoonBannerTitle: 'Certification',
    certExpiringWithin30: 'Expiring Within 30 Days',
    certAndMore: 'and {n} more',
    // Near Misses
    nearMissesTitle: 'Near Misses',
    nearMissesSubtitle: 'Track and manage safety near miss incidents across all sites',
    reportNearMiss: 'Report Near Miss',
    nearMissRecordsFound: 'record',
    nearMissFound: 'found',
    nearMissNoneFound: 'No near misses found',
    nearMissNoneFoundDesc: 'No records match your current filters.',
    nearMissColDate: 'Date',
    nearMissColSite: 'Site',
    nearMissColLocation: 'Location',
    nearMissColDescription: 'Description',
    nearMissColRisk: 'Risk',
    nearMissColStatus: 'Status',
    nearMissColReportedBy: 'Reported By',
    nearMissColPhotos: 'Photos',
    nearMissSearchPlaceholder: 'Search description or location...',
    nearMissAllSites: 'All Sites',
    nearMissAllRiskLevels: 'All Risk Levels',
    nearMissRiskHigh: 'High',
    nearMissRiskMedium: 'Medium',
    nearMissRiskLow: 'Low',
    nearMissAllStatuses: 'All Statuses',
    nearMissStatusOpen: 'Open',
    nearMissStatusUnderReview: 'Under Review',
    nearMissStatusClosed: 'Closed',
    nearMissCardTotal: 'Total Near Misses',
    nearMissModalSubtitle: 'Record a safety near miss incident',
    nearMissSelectSite: 'Select a site',
    nearMissRiskLevel: 'Risk Level',
    nearMissPhotos: 'Photos',
    nearMissPhotosHint: 'optional, up to 10',
    nearMissDragDrop: 'Drag & drop photos here, or',
    nearMissBrowseFiles: 'browse files',
    nearMissFollowUpTask: 'Create a follow-up task from this near miss',
    nearMissFollowUpTaskDesc: 'A task will be created and assigned to the site for follow-up action',
    nearMissSubmitting: 'Submitting...',
    nearMissSubmitReport: 'Submit Report',
    nearMissDetailsTitle: 'Near Miss Details',
    nearMissRiskLabel: 'Risk',
    nearMissLoadingPhotos: 'Loading photos...',
    nearMissNoPhotos: 'No photos attached',
    nearMissUpdateStatus: 'Update Status',
    nearMissUpdate: 'Update',
    // RAMS Management
    ramsTitle: 'H&S Documents',
    ramsSubtitle: 'Upload, assign and track Health & Safety document acknowledgements (policies, risk assessments, method statements, COSHH, etc.)',
    ramsTotalDocs: 'Total Docs',
    ramsAssigned: 'Assigned',
    ramsConfirmed: 'Confirmed',
    ramsPending: 'Pending',
    ramsExportPdfSection: 'Export Acknowledgement PDF',
    ramsUploadTitle: 'Upload H&S Document',
    ramsUploadSubtitle: 'PDF or Word documents up to 50MB',
    ramsDocumentTitle: 'Document Title',
    ramsSelectSite: 'Select a site...',
    ramsDocumentFile: 'Document File',
    ramsDragDropHint: 'Drop file here or click to browse',
    ramsUploading: 'Uploading...',
    ramsUploadButton: 'Upload H&S Document',
    ramsAssignTitle: 'Assign to Workers',
    ramsAssignSubtitle: 'Select document and workers to assign',
    ramsDocumentLabel: 'H&S Document',
    ramsSelectDocument: 'Select an H&S document...',
    ramsWorkers: 'Workers',
    ramsSelectAll: 'Select All',
    ramsDeselectAll: 'Deselect All',
    ramsNoWorkersForSite: 'No workers assigned to this site',
    ramsWorkersSelected: 'worker',
    ramsSelectedLabel: 'selected',
    ramsAssigning: 'Assigning...',
    ramsAssignButton: 'Assign',
    ramsAcknowledgementTracking: 'Acknowledgement Tracking',
    ramsRecord: 'record',
    ramsSearchPlaceholder: 'Search worker or document...',
    ramsAllStatus: 'All Status',
    ramsAllSites: 'All Sites',
    ramsNoRecords: 'No records found',
    ramsNoRecordsDesc: 'Upload an H&S document and assign workers to get started',
    ramsColWorker: 'Worker',
    ramsColDocument: 'H&S Document',
    ramsColSite: 'Site',
    ramsColStatus: 'Status',
    ramsColAcknowledgedAt: 'Acknowledged At',
    // Toolbox Talks
    toolboxTitle: 'Toolbox Talks Management',
    toolboxSubtitle: 'Create, assign and track worker attendance for toolbox talks',
    toolboxTotalTalks: 'Total Talks',
    toolboxAssigned: 'Assigned',
    toolboxConfirmed: 'Confirmed',
    toolboxPending: 'Pending',
    toolboxExportPdfSection: 'Export Attendance PDF',
    toolboxCreateTitle: 'Create Toolbox Talk',
    toolboxCreateSubtitle: 'Add a title, select a site, and optionally attach notes',
    toolboxTalkTitle: 'Talk Title',
    toolboxSelectSite: 'Select a site...',
    toolboxNotesDocument: 'Notes / Document',
    toolboxCreating: 'Creating...',
    toolboxCreateButton: 'Create Toolbox Talk',
    toolboxAssignSubtitle: 'Select talk and workers to assign',
    toolboxTalkLabel: 'Toolbox Talk',
    toolboxSelectTalk: 'Select a toolbox talk...',
    toolboxSelectSiteForWorkers: 'Select a toolbox talk to see workers for its site',
    toolboxAssignButton: 'Assign Toolbox Talk',
    toolboxAttendanceTracking: 'Attendance Tracking',
    toolboxSearchPlaceholder: 'Search worker or talk...',
    toolboxNoRecordsDesc: 'Create a toolbox talk and assign workers to get started',
    toolboxColTalk: 'Toolbox Talk',
    toolboxColConfirmedAt: 'Confirmed At',
    // Site Inductions
    inductionsTitle: 'Site Inductions',
    inductionsSubtitle: 'Upload, assign and track worker induction completions',
    inductionsTotalInductions: 'Total Inductions',
    inductionsAssigned: 'Assigned',
    inductionsCompleted: 'Completed',
    inductionsPending: 'Pending',
    inductionsUploadTitle: 'Upload Induction Document',
    inductionsUploadSubtitle: 'PDF or Word documents up to 100MB',
    inductionsTitleLabel: 'Induction Title',
    inductionsFileLabel: 'Induction File',
    inductionsUploadButton: 'Upload Induction',
    inductionsAssignSubtitle: 'Select induction and workers to assign',
    inductionsDocumentLabel: 'Induction Document',
    inductionsSelectInduction: 'Select an induction...',
    inductionsSelectSiteForWorkers: 'Select an induction to see workers for its site',
    inductionsAssignButton: 'Assign Induction',
    inductionsCompletionTracking: 'Completion Tracking',
    inductionsSearchPlaceholder: 'Search worker or induction...',
    inductionsNoRecordsDesc: 'Upload an induction document and assign workers to get started',
    inductionsColInduction: 'Induction',
    inductionsColCompletedAt: 'Completed At',
    // Landing page
    'landing.nav-pricing': 'Pricing',
    'landing.nav-login': 'Login',
    'landing.create-account': 'Start your 30-day free trial',
    'landing.contact-sales': "Let\'s talk",
    'landing.contact_sales': "Let\'s talk",
    // Underscore-style hero keys (used by landing/index.jsx)
    // Underscore-style problem keys
    // Underscore-style features keys
    // Underscore-style pillar keys
    // Underscore-style dashboard section keys
    // Underscore-style stat keys
    'landing.stat_expiring_certs': 'Expiring certifications',
    'landing.stat_open_incidents': 'Open incidents',
    'landing.stat_overdue_tasks': 'Overdue tasks',
    'landing.stat_outstanding_inductions': 'Outstanding inductions',
    // Underscore-style row keys
    'landing.row_rams': 'H&S document acknowledgements',
    'landing.row_rams_status': 'All signed',
    'landing.row_toolbox': 'Toolbox talks this week',
    'landing.row_toolbox_status': '2 completed',
    'landing.row_inspections': 'Site inspections',
    'landing.row_inspections_status': '1 overdue',
    'landing.row_certs': 'Worker certifications',
    'landing.row_certs_status': '3 expiring soon',
    // Underscore-style how-it-works keys
    // Underscore-style pricing keys
    'landing.pricing_tagline': 'Built for contractors managing multiple sites and teams.',
    'landing.pricing_disclaimer': 'Cancel anytime. No long-term contracts.',
    'landing.pricing_active_sites_note': 'Only active sites count toward your plan.',
    'landing.billing_annual': 'Annual',
    'landing.billing_monthly': 'Monthly',
    'landing.plan_most_popular': 'Most popular',
    'landing.plan_per_year': '/ year',
    'landing.plan_per_month': '/ month',
    'landing.plan_save_15': 'save 15%',
    'landing.plan_or_annual_prefix': 'or',
    'landing.plan_enterprise': 'Enterprise',
    'landing.plan_custom': 'Custom',
    'landing.plan_pricing': 'pricing',
    'landing.plan_tailored': 'Tailored to your portfolio',
    'landing.plan_additional_capacity': 'Add extra sites anytime — graduated volume pricing from £45/site/month, down to £25/site/month at scale.',
    'landing.plan_additional_capacity_label': 'Additional capacity:',
    // Underscore-style inspections keys
    // Underscore-style CTA keys
    // Dot-style keys (kept for backward compatibility)
    'landing.hero.badge': 'Built for Site Managers & Contractors',
    'landing.hero.h1': 'Construction Sites Are Hard Enough to Run',
    'landing.hero.subheading': 'Tasks, safety, documents, drawings and site reporting — all in one place.\n\nSave hours every week.\n\nReduce stress.\n\nStay in control of every site.',
    'landing.hero.cta-demo': 'Try Live Demo',
    'landing.hero.demo-note': 'No signup required. Explore the full system instantly.',
    'landing.hero.trust-1': 'Photo evidence on every task',
    'landing.hero.trust-2': 'Clients see verified work progress',
    'landing.hero.trust-3': 'Everything that happens on site recorded',
    'landing.problem.h2': 'Construction compliance should not live in WhatsApp, folders and spreadsheets.',
    'landing.problem.tagline': 'SiteProof brings everything together in one system.',
    'landing.problem.rams': 'H&S docs signed on paper and lost',
    'landing.problem.toolbox': 'Toolbox talks recorded inconsistently',
    'landing.problem.cscs': 'Expired CSCS cards going unnoticed',
    'landing.problem.incidents': 'Incident records scattered across emails',
    'landing.problem.photos': 'No photo evidence of work completed',
    'landing.problem.inspections': 'Site inspections buried in paperwork',
    'landing.features.eyebrow': 'Platform features',
    'landing.features.h2': "Everything your site needs. Nothing it doesn't.",
    'landing.pillar.site-management': 'Site Management',
    'landing.pillar.daily-tasks': 'Daily tasks',
    'landing.pillar.snag-management': 'Snag management',
    'landing.pillar.site-diary': 'Site diary',
    'landing.pillar.checklists': 'Checklists',
    'landing.pillar.drawings-plans': 'Drawings and plans',
    'landing.pillar.safety-compliance': 'Safety & Compliance',
    'landing.pillar.rams': 'H&S doc acknowledgement',
    'landing.pillar.incident-reporting': 'Incident reporting',
    'landing.pillar.site-inductions': 'Site inductions',
    'landing.pillar.toolbox-talks': 'Toolbox talks',
    'landing.pillar.inspections-audits': 'Inspections and audits',
    'landing.pillar.compliance-reporting': 'Compliance reporting',
    'landing.pillar.workforce-management': 'Workforce Management',
    'landing.pillar.worker-certs': 'Worker certifications',
    'landing.pillar.expiry-alerts': 'Expiry alerts',
    'landing.pillar.worker-dashboard': 'Worker dashboard',
    'landing.pillar.team-invites': 'Team invites',
    'landing.pillar.role-access': 'Role-based access',
    'landing.pillar.reporting-oversight': 'Reporting & Oversight',
    'landing.pillar.audit-trail': 'Activity audit trail',
    'landing.pillar.task-analytics': 'Task analytics',
    'landing.pillar.compliance-reports': 'Compliance reports',
    'landing.pillar.pdf-csv': 'PDF/CSV exports',
    'landing.pillar.realtime-notifications': 'Real-time notifications',
    'landing.dashboard.eyebrow': 'Compliance dashboard',
    'landing.dashboard.h2': 'See compliance across all your sites at a glance.',
    'landing.dashboard.subheading': 'Track expiring certifications, open incidents, overdue tasks, inspections and outstanding inductions from one dashboard.',
    'landing.dashboard.cta-demo': 'Try Live Demo',
    'landing.dashboard.demo-note': 'No signup required. Explore the full system instantly.',
    'landing.dashboard.stat.expiring-certs': 'Expiring certifications',
    'landing.dashboard.stat.open-incidents': 'Open incidents',
    'landing.dashboard.stat.overdue-tasks': 'Overdue tasks',
    'landing.dashboard.stat.outstanding-inductions': 'Outstanding inductions',
    'landing.row.rams': 'H&S document acknowledgements',
    'landing.row.rams.status': 'All signed',
    'landing.row.toolbox': 'Toolbox talks this week',
    'landing.row.toolbox.status': '2 completed',
    'landing.row.inspections': 'Site inspections',
    'landing.row.inspections.status': '1 overdue',
    'landing.row.certs': 'Worker certifications',
    'landing.row.certs.status': '3 expiring soon',
    'landing.hiw.eyebrow': 'Simple process',
    'landing.hiw.h2': 'How SiteProof Works',
    'landing.hiw.step1.title': 'Create your company and sites',
    'landing.hiw.step1.desc': 'Set up your company profile, add your construction sites and configure compliance requirements in minutes.',
    'landing.hiw.step2.title': 'Invite your team and upload compliance documents',
    'landing.hiw.step2.desc': 'Add workers, site managers and subcontractors. Upload H&S documents, certifications and induction materials for your team.',
    'landing.hiw.step3.title': 'Track safety, tasks and compliance in real time',
    'landing.hiw.step3.desc': 'Monitor incidents, inspections, toolbox talks, certifications and task progress across all your sites from one dashboard.',
    'landing.insp.eyebrow': 'Inspections & Reporting',
    'landing.insp.h2': 'Complete inspections and generate reports instantly.',
    'landing.insp.subheading': 'Run site inspections, record pass/fail responses, add notes and photos, and export professional PDF reports for audits, compliance checks and site reviews.',
    'landing.insp.point.1': 'Pass / Fail / N/A responses per inspection item',
    'landing.insp.point.2': 'Photo evidence and notes captured on site',
    'landing.insp.point.3': 'GPS location recorded per item',
    'landing.insp.point.4': 'Professional PDF reports generated instantly',
    'landing.insp.point.5': 'Full audit trail for every completed inspection',
    'landing.insp.item.1': 'Fire extinguisher in place',
    'landing.insp.item.2': 'PPE worn by all workers',
    'landing.insp.item.3': 'Scaffold edge protection',
    'landing.insp.item.4': 'Hazard signage visible',
    'landing.insp.item.5': 'First aid kit accessible',
    'landing.insp.pass': 'Pass',
    'landing.insp.fail': 'Fail',
    'landing.insp.na': 'N/A',
    'landing.insp.summary': '4 of 5 items passed',
    'landing.insp.export-pdf': 'Export PDF',
    'landing.pricing.eyebrow': 'Simple pricing',
    'landing.pricing.h2': 'Simple pricing based on active construction sites',
    'landing.pricing.subheading': 'Unlimited users. No hidden fees. Only active sites count toward your plan.',
    'landing.pricing.tagline': 'Built for contractors managing multiple sites and teams.',
    'landing.pricing.disclaimer': 'Cancel anytime. No long-term contracts.',
    'landing.billing.annual': 'Annual',
    'landing.billing.monthly': 'Monthly',
    'landing.pricing.active-sites-note': 'Only active sites count toward your plan.',
    'landing.plan.most-popular': 'Most popular',
    'landing.plan.per-year': '/ year',
    'landing.plan.per-month': '/ month',
    'landing.plan.save-15': 'save 15%',
    'landing.plan.or-annual-prefix': 'or',
    'landing.plan.enterprise': 'Enterprise',
    'landing.plan.custom': 'Custom',
    'landing.plan.pricing': 'pricing',
    'landing.plan.tailored': 'Tailored to your portfolio',
    'landing.plan.additional-capacity': 'Add extra sites anytime — graduated volume pricing from £45/site/month, down to £25/site/month at scale.',
    'landing.plan.additional-capacity-label': 'Additional capacity:',
    'landing.cta.h2': 'Take control of site compliance.',
    'landing.cta.subheading': 'SiteProof is built for site managers and contractors who need photo evidence, snag tracking and compliance records — all in one place.',
    // Demo login page
    'demo.back-to-home': 'Back to home',
    'demo.sign-in-instead': 'Sign in instead',
    'demo.live-demo-badge': 'Live Demo',
    'demo.choose-role': 'Choose your demo role',
    'demo.signing-in-as': 'Signing in as',
    'demo.demo-user-label': 'Demo user:',
    'demo.footer-note': 'Demo accounts are shared. If login fails,',
    'demo.footer-link': 'go to the login page',
    'demo.footer-note2': 'and use "Create / Reset Demo Users" then try again.',
    'demo.want-own-account': 'Want your own account?',
    'demo.register-company': 'Register your company',
    // Demo role cards
    'demo.role.admin-title': 'Admin',
    'demo.role.admin-badge': 'Company Admin',
    'demo.role.admin-desc': 'Full access to all sites, compliance records, workers, documents and reports.',
    'demo.role.manager-title': 'Site Manager',
    'demo.role.manager-badge': 'Site Manager',
    'demo.role.manager-desc': 'Manage daily tasks, inspections, snags and team activity for your assigned sites.',
    'demo.role.worker-title': 'Worker',
    'demo.role.worker-badge': 'Site Worker',
    'demo.role.worker-desc': 'Complete daily tasks, upload photo evidence, report snags and view your inductions.',
    'demo.role.client-title': 'Client',
    'demo.role.client-badge': 'Client / Visitor',
    'demo.role.client-desc': 'Read-only view of site progress, compliance status, snags and documents shared with you.',
    // Demo login page — underscore-style keys (used by demo-login/index.jsx)
    'demo.subtitle_underscore': 'Explore SiteProof from this role with demo data',
    // Demo role cards — underscore-style
    // Dashboard quick action buttons
    dashBtnRAMS: 'H&S Docs',
    dashBtnInductions: 'Inductions',
    dashBtnToolboxTalks: 'Toolbox Talks',
    dashBtnSiteDiary: 'Site Diary',
    dashBtnCertifications: 'Certifications',
    dashBtnComplianceDashboard: 'Compliance Dashboard',
    dashBtnNearMisses: 'Near Misses',
    dashBtnAddNewSite: 'Add New Site',
    // Dashboard stat cards
    dashCardTotalSites: 'Active Sites',
    dashCardCompliantSites: 'Compliant Sites',
    dashCardNonCompliant: 'Non-Compliant Sites',
    dashCardOpenSnags: 'Open Snags',
    dashCardOverdueTasks: 'Overdue Tasks',
    dashCardExpiringDocs: 'Expiring Documents',
    dashCardIncidents7d: 'Incidents (Last 7 Days)',
    dashCardWorkersOnSite: 'Workers On Site',
    dashCardWithin30Days: 'Within 30 days',
    dashCardThisWeek: 'This week',
    dashCardToday: 'Today',
    // Site manager dashboard
    siteManagerReportIncident: 'Report Incident',
    // Site manager tabs
    smTabOverview: 'Overview',
    smTabDocuments: 'Documents',
    smTabDrawings: 'Drawings',
    smTabSnags: 'Snags',
    smTabIncidents: 'Incidents',
    smTabReports: 'Reports',
    // Site manager quick actions
    smQaAddPhoto: 'Add Photo',
    smQaCreateSnag: 'Create Snag',
    smQaReportIncident: 'Report Incident',
    smQaAssignTask: 'Assign Task',
    smQaSiteDiary: 'Site Diary',
    smQaRAMS: 'H&S Docs',
    smQaInductions: 'Inductions',
    smQaToolboxTalks: 'Toolbox Talks',
    smQaCertifications: 'Certifications',
    smQaCompliance: 'Compliance',
    smQaNearMisses: 'Near Misses',
    // Site manager stat cards
    smCardDocuments: 'Documents',
    smCardOpenSnags: 'Open Snags',
    smCardDrawings: 'Drawings',
    smCardWorkers: 'Workers',
    smCardViewAll: 'View All',
    // Site status / detail
    siteStatusCompliant: 'Compliant',
    siteFullDetails: 'Full Details',
    // Team panel
    teamTitle: 'Team',
    teamManage: 'Manage',
    teamFilterAll: 'All',
    teamFilterWorkers: 'Workers',
    teamFilterManage: 'Managers',
    teamRoleSiteManager: 'Site Manager',
    teamRoleWorker: 'Worker',
    // Task progress panel
    taskProgressTitle: 'Task Progress',
    taskProgressViewAll: 'View All',
    taskProgressTotal: 'Total',
    taskProgressDone: 'Done',
    taskProgressBlocked: 'Blocked',
    taskProgressOverdue: 'Overdue',
    taskProgressNoTasks: 'No tasks for this site',
    taskProgressPlan: 'Plan',
    taskProgressTodayCompletion: 'Today\'s Completion',
    // Site health panel
    siteHealthTitle: 'Site Health',
    siteHealthSubtitle: 'Live compliance overview',
    siteHealthRisk: 'Risk',
    siteHealthComplianceScore: 'Compliance Score',
    siteHealthOpenSnags: 'Open Snags',
    siteHealthIncidents7d: 'Incidents (7 Days)',
    // Site overview
    siteOverallCompletion: 'Overall Completion',
    siteClient: 'Client',
    siteStatus: 'Status',
    // Safety overview panel
    safetyCompliance: 'Safety & Compliance',
    safetySubtitle: 'Compliance actions required today',
    safetyReportIncident: 'Report Incident',
    safetyViewAll: 'View All',
    // Compliance items
    smComplianceSiteDiary: 'Site Diary',
    smComplianceDailyPhotos: 'Daily Photos',
    smComplianceToolboxSign: 'Toolbox Talk Sign-Off',
    smComplianceSiteChecks: 'Site Checks',
    // Status labels
    SmStatusCompleted: 'Completed',
    SmStatusPending: 'Pending',
    SmStatusOverdue: 'Overdue',
    // Documents
    documentsNeedingAttention: 'Documents Needing Attention',
    viewAll: 'View all',
    replace: 'Replace',
    updateDate: 'Update date',
    // Site health AI
    siteHealthAIInsights: 'AI Insights',
    siteHealthAINonInsights: 'No insights available',
    siteHealthAIPowered: 'AI Powered',
    allDocumentsUpToDate: 'All documents up to date',
    noDocumentsExpiring: 'No documents expiring soon',
    siteHealthOverdueIns: 'Overdue Inductions',
    siteHealthExpiringDocs: 'Expiring Documents',
    smComplianceActions: 'Compliance Actions',
    smComplianceActionsSubtitle: 'Actions required today',
    // Worker onboarding
    onboardingWelcome: 'Welcome',
    onboardingSubtitle: 'Get started with your daily tasks',
    onboardingCompleteProfile: 'Complete Profile',
    onboardingViewAssignments: 'View Assignments',
    onboardingSubmitCheckin: 'Submit Check-In',
    onboardingDismiss: 'Dismiss',
    // Email verification pending
    // Login page
    'login.or_sign_in': 'Or sign in to your account',
    'LOGIN.ROLE_BASED_ACCESS': 'ROLE-BASED ACCESS',
    // Login page — additional strings
    'login.early_access': 'Early Access',
    // Login — forgot password modal
    // Login — disclaimer
    // Reset password page
    // Company registration page
    // Email verified page
    // Invoice history page
    failedToLoadInvoices: 'Failed to load invoices',
    backToSubscription: 'Back to subscription',
    invoiceHistory: 'Invoice History',
    invoiceHistorySubtitle: 'View and download your past invoices',
    noInvoicesYet: 'No invoices yet',
    noInvoicesYetDesc: 'Your invoices will appear here once your first billing cycle is complete.',
    invoice: 'Invoice',
    period: 'Period',
    amount: 'Amount',
    downloadPdf: 'Download PDF',
    viewInvoice: 'View invoice',
    // Task analytics dashboard
    taskAnalyticsSubtitle: 'Track task completion rates, worker performance and site progress',
    exportPdf: 'Export PDF',
    planTasks: 'Plan Tasks',
    sitesNeedAttention: 'sites need attention',
    siteNeedsAttention: 'site needs attention',
    totalToday: 'Total Today',
    completed: 'Completed',
    blocked: 'Blocked',
    completionRate: 'Completion Rate',
    requiredRate: 'Required Rate',
    categoryPerformance: 'Category Performance',
    categoryPerformanceSubtitle: 'Task completion broken down by category',
    trends: 'Trends',
    byCategory: 'By Category',
    todayTaskDistribution: 'Today\'s task distribution',
    noTaskDataToday: 'No task data for today',
    onTrack: 'On Track',
    atRisk: 'At Risk',
    sitePerformance: 'Site Performance',
    clickSiteToViewTasks: 'Click a site to view its tasks',
    done: 'Done',
    noSiteDataAvailable: 'No site data available',
    completion: 'completion',
    completionTrend: 'Completion Trend',
    last14Days: 'Last 14 days',
    last7Days: 'Last 7 days',
    dailyCompletionRate: 'daily completion rate',
    workerProductivity: 'Worker Productivity',
    clickWorkerToViewHistory: 'Click a worker to view their task history',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    noTasksAssignedToday: 'No tasks assigned today',
    avgCompletion: 'avg completion',
    // Drawings & Plans
    drawingsPlans: 'Drawings & Plans',
    drawingsPlansSubtitle: 'Manage and track all drawing revisions across your sites',
    galleryView: 'Gallery View',
    uploadDrawing: 'Upload Drawing',
    filterDrawings: 'Filter Drawings',
    searchDrawingsPlaceholder: 'Search by name, number or site…',
    filterByType: 'Filter by Type',
    allTypes: 'All Types',
    showLatestOnly: 'Show latest revision only',
    noDrawingsFound: 'No drawings found',
    noMatchingDrawings: 'No drawings match your filters',
    getStartedUploadDrawing: 'Upload your first drawing to get started',
    noDrawingsForSite: 'No drawings have been uploaded for this site yet',
    tryAdjustingFilters: 'Try adjusting your search or filter criteria',
    architectural: 'Architectural',
    structural: 'Structural',
    mechanical: 'Mechanical',
    electrical: 'Electrical',
    plumbing: 'Plumbing',
    sitePlan: 'Site Plan',
    other: 'Other',
    home: 'Home',
    // Subscription & Billing
    subscriptionBilling: 'Subscription & Billing',
    subscriptionBillingSubtitle: 'Manage your plan, billing cycle and site usage',
    currentPlan: 'Current Plan',
    currentPlanSubtitle: 'Your active subscription and billing details',
    noActiveSubscription: 'No active subscription',
    noActiveSubscriptionDesc: 'Complete checkout to activate your plan and unlock all features.',
    choosePlanAndPay: 'Choose a plan and pay',
    changePlan: 'Change Plan',
    changePlanSubtitle: 'Switch to a different plan at any time',
    selectedPlan: 'Selected plan',
    // Additional billing keys
    plan: 'Plan',
    billingCycle: 'Billing Cycle',
    renewalDate: 'Renewal Date',
    includedSites: 'Included Sites',
    siteUsageBillingBreakdown: 'Site Usage & Billing Breakdown',
    activeSitesVsAllowance: 'Active sites vs allowance',
    usingPercentOfAllowance: 'Using {pct}% of your site allowance',
    basePlan: 'Base Plan',
    includesUpTo: 'Includes up to',
    activeSitesLabel: 'active sites',
    extraActiveSites: 'Extra Active Sites',
    noExtraSites: 'No extra sites',
    each: 'each',
    monthly: 'Monthly',
    annual: 'Annual',
    billing: 'Billing',
    manageSubscription: 'Manage Subscription',
    manageSubscriptionDesc: 'Update payment method, download invoices and manage your subscription via the Stripe billing portal.',
    opening: 'Opening…',
    billingPortalAvailableAfterPayment: 'The billing portal will be available once your first payment is processed.',
    subscriptionCanceled: 'Cancels at end of period',
    upTo: 'Up to',
    current: 'Current plan',
    switch: 'Switch',

    // ── Snags ──────────────────────────────────────────────────────────────
    snags: 'Snags',
    snagsSubtitle: 'Track, manage, and resolve site issues',
    exportPDF: 'Export PDF',
    reportSnag: 'Report Snag',
    reportNewSnag: 'Report New Snag',
    allSnags: 'All Snags',
    noSnagsFound: 'No snags found',
    noSnagsFoundDesc: 'There are currently no snags recorded for this site',
    noSnagsRecorded: 'No snags recorded',
    noSnagsDesc: 'No snags have been reported for this site yet',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    open: 'Open',
    total: 'Total Snags',
    snagResolution: 'Snag Resolution',
    resolutionProgress: 'Resolution Progress',
    snagDetail: 'Snag Detail',
    snagDetails: 'Snag Details',
    searchSnagsPlaceholder: 'Search snags by title, description or location…',
    allStatuses: 'All Statuses',
    allPriorities: 'All Priorities',
    allSites: 'All Sites',
    photo: 'photo',
    photos: 'photos',
    unassigned: 'Unassigned',
    viewAllSnags: 'View All Snags',
    // Snag detail
    reportedBy: 'Reported by',
    assignedTo: 'Assigned to',
    unknownSite: 'Unknown Site',
    unknown: 'Unknown',
    markInProgress: 'Mark In Progress',
    markResolved: 'Mark Resolved',
    updating: 'Updating…',
    resolvedOn: 'Resolved on',
    by: 'by',
    resolveSnag: 'Resolve Snag',
    resolveSnagPrompt: 'Add resolution notes and mark this snag as resolved.',
    resolveSnagPlaceholder: 'Describe how this snag was resolved…',
    resolving: 'Resolving…',
    confirmResolved: 'Confirm Resolved',
    location: 'Location',
    // Snag status badge
    Snags: 'Snags',

    // ── Tasks ──────────────────────────────────────────────────────────────
    tasks: 'Tasks',
    tasksSubtitle: 'Manage and track daily site tasks',
    noTasksFound: 'No tasks found',
    noTasksFoundDesc: 'No tasks have been assigned yet',
    taskDetails: 'Task Details',
    taskStatus: 'Task Status',
    taskPriority: 'Task Priority',
    taskAssignedTo: 'Assigned To',
    taskDueDate: 'Due Date',
    taskCompleted: 'Completed',
    taskPending: 'Pending',
    taskOverdue: 'Overdue',
    taskBlocked: 'Blocked',
    addTask: 'Add Task',
    createTask: 'Create Task',
    editTask: 'Edit Task',
    deleteTask: 'Delete Task',
    markComplete: 'Mark Complete',
    viewTask: 'View Task',

    // ── Documents ──────────────────────────────────────────────────────────
    documentManagement: 'Documents',
    documentManagementSubtitle: 'Upload, manage and track all site compliance documents',
    documentHeader: 'Document',
    tableView: 'Table view',
    gridView: 'Grid view',
    owner: 'Owner',
    issueDate: 'Issue Date',
    expiryDate: 'Expiry Date',
    uploadDocument: 'Upload Document',
    editDocument: 'Edit Document',
    deleteDocument: 'Delete Document',
    downloadDocument: 'Download Document',
    documentExpired: 'Expired',
    documentExpiringSoon: 'Expiring Soon',
    documentValid: 'Valid',
    allCategories: 'All Categories',

    // ── Incidents ──────────────────────────────────────────────────────────
    incidentReporting: 'Incidents & Near Misses',
    incidentReportingSubtitle: 'Report and track site incidents, near misses and safety events',
    reportIncident: 'Report Incident',
    viewReports: 'View Reports',
    incidentReports: 'Incident Reports',
    noIncidentsRecorded: 'No incidents recorded',
    noIncidentsDesc: 'No incidents have been reported for this site yet',
    immediateAction: 'Immediate action taken',
    allSeverities: 'All Severities',
    nearMiss: 'Near Miss',
    injury: 'Injury',
    propertyDamage: 'Property Damage',
    unsafeAct: 'Unsafe Act',
    unsafeCondition: 'Unsafe Condition',

    // ── Company Settings ───────────────────────────────────────────────────
    companySettingsTitle: 'Company Settings',
    companySettingsSubtitle: 'Manage your company profile and preferences',
    companyName: 'Company Name',
    companyAddress: 'Company Address',
    companyPhone: 'Company Phone',
    companyEmail: 'Company Email',
    settingsSaved: 'Settings saved successfully',
    settingsError: 'Failed to save settings',
    dangerZone: 'Danger Zone',
    deleteCompany: 'Delete Company',
    deleteCompanyDesc: 'Permanently delete your company and all associated data. This action cannot be undone.',
    moduleToggles: 'Module Toggles',
    moduleTogglesSubtitle: 'Enable or disable features for your company',
    photoEvidenceSettings: 'Photo Evidence Settings',
    requirePhotoEvidence: 'Require photo evidence for task completion',
    requirePhotoEvidenceSnags: 'Require photo evidence for snag reports',
    clientVisibility: 'Client Visibility',
    clientVisibilitySubtitle: 'Control what clients can see in their portal',

    // ── Profile Settings ───────────────────────────────────────────────────
    profileSettingsTitle: 'Profile Settings',
    profileSettingsSubtitle: 'Update your personal information and preferences',
    fullName: 'Full Name',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    updatePassword: 'Update Password',
    notificationPreferences: 'Notification Preferences',
    emailNotifications: 'Email Notifications',
    pushNotifications: 'Push Notifications',

    // ── Client Portal ──────────────────────────────────────────────────────
    clientPortal: 'Client Portal',
    clientPortalSubtitle: 'View your project progress and site updates',
    clientDashboard: 'Client Dashboard',
    projectProgress: 'Project Progress',
    siteUpdates: 'Site Updates',
    sharedDocuments: 'Shared Documents',
    complianceStatus: 'Compliance Status',
    noUpdatesYet: 'No updates yet',
    noUpdatesYetDesc: 'Site updates will appear here as work progresses',
    noSharedDocuments: 'No shared documents',
    noSharedDocumentsDesc: 'Documents shared with you will appear here',

    // ── Shared / Common ────────────────────────────────────────────────────
    exporting: 'Exporting…',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    critical: 'Critical',
    allPrioritiesLabel: 'All Priorities',
    photo_count: '{n} photos',
    noRecordsFound: 'No records found',

    // ── Snag form labels & placeholders ───────────────────────────────────
    title: 'Title',
    snagTitlePlaceholder: 'e.g. Damaged fencing near entrance',
    assignTo: 'Assign To',
    locationWithinSite: 'Location Within Site',
    locationPlaceholder: 'e.g. Block A, Level 2',
    snagDescriptionPlaceholder: 'Describe the issue in detail…',
    tapToAddPhotos: 'Tap to add photos, or',
    browse: 'browse',
    photoUploadHint: 'JPG, PNG supported — up to 10 photos',
    uploadingPhotos: 'Uploading photos…',
    creating: 'Creating…',
    createSnag: 'Create Snag',
    siteManager: 'Site Manager',
    titleRequired: 'Title is required',
    descriptionRequired: 'Description is required',

    // ── Incident form labels & placeholders ───────────────────────────────
    reportNewIncident: 'Report New Incident',
    reportNewIncidentSubtitle: 'Record a site incident, near miss or safety event',
    incidentType: 'Incident Type',
    dateAndTime: 'Date & Time',
    severityLevel: 'Severity Level',
    locationDescription: 'Location Description',
    locationDescriptionPlaceholder: 'e.g. Block A, Level 2, near stairwell',
    incidentDescription: 'Incident Description',
    incidentDescriptionPlaceholder: 'Describe what happened, the sequence of events, and any contributing factors…',
    personnelInvolvement: 'Personnel Involvement',
    injuredParty: 'Injured Party',
    injuredPartyPlaceholder: 'Name of injured or affected person (if applicable)',
    witnessName: 'Witness Name',
    witnessNamePlaceholder: 'Full name of witness',
    witnessContact: 'Witness Contact',
    witnessContactPlaceholder: 'Phone number or email',
    actionsTaken: 'Actions Taken',
    immediateActionTaken: 'Immediate Action Taken',
    immediateActionPlaceholder: 'Describe the immediate steps taken at the scene…',
    correctiveMeasures: 'Corrective Measures',
    correctiveMeasuresPlaceholder: 'Describe any corrective or preventive actions planned…',
    attachmentsOptional: 'Attachments (Optional)',
    clickToUploadFiles: 'Click to upload files',
    fileUploadHint: 'Photos and PDFs accepted — max 10MB each',
    submitReport: 'Submit Report',
    failedToSubmitReport: 'Failed to submit report. Please try again.',
    errorSubmittingReport: 'An error occurred while submitting the report.',
    incidentTypeRequired: 'Please select an incident type',
    dateTimeRequired: 'Date and time are required',
    locationDescriptionRequired: 'Location description is required',
    incidentDescriptionRequired: 'Incident description is required',
    severityLevelRequired: 'Please select a severity level',

    // ── Document upload / edit modal ──────────────────────────────────────
    uploadDocumentSubtitle: 'Upload a compliance document to this site',
    updateDocumentDetails: 'Update document information',
    documentTitle: 'Document Title',
    enterDocumentTitle: 'e.g. Public Liability Insurance 2024',
    ownerResponsibleParty: 'Owner / Responsible Party',
    enterOwnerName: 'e.g. John Smith',
    contractorCompanyName: 'Contractor / Company Name',
    contractorOrCompanyName: 'e.g. ABC Contractors Ltd',
    certificateNumber: 'Certificate Number',
    policyNumber: 'Policy Number',
    inspectionDate: 'Inspection Date',
    optionalNotesAboutDocument: 'Optional notes about this document…',
    additionalNotesAboutDocument: 'Additional notes about this document…',
    file: 'File',
    clickToSelectFile: 'Click to select a file',
    pdfDocJpgPngMax10mb: 'PDF, DOC, DOCX, JPG, PNG — max 10MB',
    analysingDocument: 'Analysing document with AI…',
    aiPrefilledFields: 'Fields pre-filled by AI — please review before saving',
    aiAutofillUnavailable: 'AI document analysis is not available. Please fill in the fields manually.',
    uploading: 'Uploading…',

    // ── Near Miss form ────────────────────────────────────────────────────
    nearMissLocationPlaceholder: 'e.g. Level 3, East stairwell',
    nearMissDescriptionPlaceholder: 'Describe what happened, what could have gone wrong, and any contributing factors…',

    // ── RAMS / Toolbox / Induction upload panels ──────────────────────────
    dragDropOrBrowse: 'Drop file here or click to browse',

    // ── Company Settings (hardcoded strings) ─────────────────────────────
    companySettingsPageTitle: 'Company Settings',
    companySettingsPageSubtitle: 'Manage your company profile and preferences',
    subscriptionAndBilling: 'Subscription & Billing',
    billingStatus: 'Billing Status',
    activeSitesLabel2: 'Active Sites',
    currentPlanLabel: 'Current Plan',
    manageSubscriptionBtn: 'Open Billing Portal',
    manageSubscriptionDesc2: 'Update your payment method, view invoices, or cancel your plan.',
    companyInformation: 'Company Information',
    companyNameLabel: 'Company Name',
    companyNamePlaceholder: 'Your company name',
    contactEmailLabel: 'Contact Email',
    contactEmailPlaceholder: 'contact@yourcompany.com',
    companyDetailsSaved: 'Company details saved successfully.',
    siteComplianceSettings: 'Site & Compliance Settings',
    siteComplianceSettingsDesc: 'These settings apply to all workers and site managers in your company.',
    requirePhotoTaskLabel: 'Require photo evidence for task completion',
    requirePhotoTaskDesc: 'Workers must upload at least one photo before marking a task as complete.',
    requirePhotoSnagLabel: 'Require photo evidence when raising a snag',
    requirePhotoSnagDesc: 'A snag cannot be submitted without at least one photo attached.',
    requireToolboxSignoffLabel: 'Require toolbox talk sign-off before starting tasks',
    requireToolboxSignoffDesc: 'Workers must confirm all assigned toolbox talks before they can access their task list.',
    enableIncidentReportingLabel: 'Enable incident reporting for workers',
    settingsSavedSuccess: 'Settings saved successfully.',
    saveSettings: 'Save Settings',
    deleteAccountBtn: 'Delete Account',
    deleteAccountDesc: 'Permanently delete your company account. This will immediately cancel any active Stripe subscription and remove all company data. This action cannot be undone.',
    deleteAccountConfirmLabel: 'Type',
    deleteAccountConfirmLabel2: 'to confirm',
    deleteAccountPermanent: 'This will permanently delete your account.',
    deleteAccountBullet1: 'Your active Stripe subscription will be cancelled immediately',
    deleteAccountBullet2: 'All company data, sites, workers, and documents will be removed',
    deleteAccountBullet3: 'This cannot be undone',
    deletingAccount: 'Deleting account…',
    permanentlyDeleteAccount: 'Permanently Delete Account',
    advancedSettingsAdminOnly: 'Advanced company settings and billing management are available to account administrators.',
    usedPercent: '% used',

    // ── Profile Settings (hardcoded strings) ──────────────────────────────
    profileSettingsPageTitle: 'Profile Settings',
    profileSettingsPageSubtitle: 'Manage your account details and notification preferences',
    yourAccount: 'Your Account',
    fullNameLabel: 'Full Name',
    emailAddressLabel: 'Email Address',
    roleLabel: 'Role',
    // ── Drawing upload modal ──────────────────────────────────────────────
    uploadDrawingSubtitle: 'Upload a drawing for this site and track revisions',
    drawingName: 'Drawing Name',
    drawingNamePlaceholder: 'e.g. Ground Floor Layout',
    drawingNumber: 'Drawing Number',
    drawingNumberPlaceholder: 'e.g. A-101',
    revision: 'Revision',
    revisionPlaceholder: 'e.g. Rev B',
    drawingDescriptionPlaceholder: 'Optional description of this drawing',
    discipline: 'Discipline',
    selectDiscipline: 'Select discipline…',
    selectFile: 'Select File',
    onlyPdfAndImageAllowed: 'Only PDF, JPG, PNG or WebP files are allowed',
    fileSizeTooLarge: 'File size must be under 50 MB',
    drawingNameRequired: 'Drawing name is required',
    drawingNumberRequired: 'Drawing number is required',
    revisionRequired: 'Revision is required',
    siteRequired: 'Please select a site',
    disciplineRequired: 'Please select a discipline',
    selectFileRequired: 'Please select a file to upload',
    versionControl: 'Version Control',
    versionControlDesc: 'Uploading a drawing with the same drawing number will create a new revision. Previous versions are preserved and accessible via the version history.',
    civil: 'Civil',
    fireProtection: 'Fire Protection',
    landscape: 'Landscape',
    // Drawing card & versions modal
    drawingNo: 'Drawing No.',
    uploaded: 'Uploaded',
    size: 'Size',
    openViewer: 'Open Viewer',
    viewAllVersions: 'View All Versions',
    versionHistory: 'Version History',
    noVersionsFound: 'No versions found for this drawing',
    latest: 'Latest',
    // Drawing detail page
    drawingMetadata: 'Drawing Information',
    revisionHistory: 'Revision History',
    revisions: 'revisions',
    noRevisionsFound: 'No revisions found',
    version: 'Version',
    viewing: 'Viewing',
    uploadNewRevision: 'Upload New Revision',
    dragDropOr: 'Drag & drop or',
    pdfPngJpgWebpSupported: 'PDF, PNG, JPG, WebP supported',
    uploadRevision: 'Upload Revision',
    drawingNotFound: 'Drawing not found',
    failedToLoadDrawing: 'Failed to load drawing',
    goBack: 'Go back',
    drawingDetail: 'Drawing Detail',
    rev: 'Rev',
    // Site detail — Documents tab
    siteDocuments: 'Site Documents',
    addDocument: 'Add Document',
    uploadError: 'Upload failed',
    titleHeader: 'Title',
    // Site detail — Drawings tab

    // ── Site Detail Dashboard ─────────────────────────────────────────────
    report: 'Export Report',
    settings: 'Site Settings',
    overallProgress: 'Overall Progress',
    activityTimeline: 'Activity Timeline',
    clientAccess: 'Client Access',
    totalDocuments: 'Total Documents',
    criticalIncidents: 'Critical Incidents',
    completedAudits: 'Completed Audits',
    siteProgressOverview: 'Site Progress Overview',
    siteTimelineProgress: 'Schedule Progress',
    siteWorkCompleted: 'Work Completed',
    noTasksYet: 'No tasks yet',
    siteManagement: 'Site Management',
    allClear: 'All Clear',
    noUrgentIssues: 'No urgent issues at this time',
    viewAllActivity: 'View All Activity',
    siteDetail: 'Site Detail',
    dailyTasks: 'Daily Tasks',
    incidents: 'Incidents',
    auditsInspections: 'Audits & Inspections',
    startDate: 'Start Date',
    endDate: 'End Date',
    client: 'Client',
    documentsExpiringSoon: 'Documents Expiring Soon',
    documentRequireAttention: 'document(s) require attention',
    criticalIncidentReported: 'critical incident(s) reported',
    checklistCompletion: 'Checklist Completion',
    urgentIssues: 'Urgent Issues',
    noActivityYet: 'No activity recorded yet',
    recentActivity: 'Recent Activity',

    // ── Site Manager Dashboard ────────────────────────────────────────────
    siteManagerSubtitle: 'Site Manager',
    siteManagerNoSite: 'No site assigned',
    siteManagerManagingSites: 'Managing {n} sites',

    // ── Site Dashboard (legacy) ───────────────────────────────────────────
    audits: 'Audits',
    drawings: 'Drawings',
    checklists: 'Checklists',
    actionRequired: 'Action Required',

    // ── Workers Table ─────────────────────────────────────────────────────
    worker: 'Worker',
    allWorkers: 'All Workers',
    inducted: 'Inducted',
    pendingInduction: 'Pending Induction',
    certificatesExpiring: 'Certificates Expiring',
    inductionDate: 'Induction Date',
    inductionStatus: 'Induction Status',
    certificates: 'Certificates',
    certificateStatus: 'Certificate Status',
    contact: 'Contact',
    noSiteAssigned: 'No site assigned',

    // ── Enhanced Admin Dashboard ──────────────────────────────────────────────
    tasksDone: 'Tasks Done',
    snagsRaisedClosed: 'Snags Raised / Closed',
    avgTime: 'Avg. Time',
    docsPercent: 'Docs %',
    score: 'Score',
    auditPack: 'Audit Pack Export',
    preparing: 'Preparing…',

    // ── Document Management — missing keys ────────────────────────────────────
    searchDocumentsPlaceholder: 'Search documents…',
    noDocumentsYet: 'No documents yet',
    noDocumentsYetDesc: 'Upload your first compliance document to get started.',
    uploadFirstDocument: 'Upload Document',
    noDocumentsFound: 'No documents found',
    noDocumentsFoundDesc: 'No documents match your current search or filters. Try adjusting your criteria.',
    clearFilters: 'Clear Filters',
    clearAllFilters: 'Clear all filters',
    insurance: 'Insurance',
    trainingCertificates: 'Training Certificates',
    plantCertificates: 'Plant Certificates',
    allStatus: 'All Statuses',
    valid: 'Valid',
    expiringSoon30Days: 'Expiring Soon (30 days)',
    expired: 'Expired',
    chooseAction: 'Choose action…',
    changeCategory: 'Change Category',
    deleteSelected: 'Delete Selected',
    documentSingular: 'document',
    documentPlural: 'documents',
    selected: 'selected',
    clearSelection: 'Clear selection',
    closeModal: 'Close',
    saveChanges: 'Save Changes',
    download: 'Download',

    // ── Site Detail Dashboard — missing keys ──────────────────────────────────
    noDocumentsDesc: 'Upload your first document to get started.',
    noDrawingsYet: 'No drawings yet',
    noDrawingsDesc: 'Upload your first drawing to get started.',
    newAudit: 'New Audit',
    auditor: 'Auditor',
    noAuditsConducted: 'No audits conducted',
    noAuditsDesc: 'No inspections or audits have been recorded for this site yet.',
    createTemplate: 'Create Template',
    completeTasks: 'Complete Tasks',
    items: 'items',
    completedBy: 'Completed by',
    at: 'at',
    noChecklistsConfigured: 'No checklists configured',
    noChecklistsDesc: 'No daily task checklists have been set up for this site yet.',
    createChecklistTemplate: 'Create Checklist Template',
    templateName: 'Template Name',
    descriptionOptional: 'Description (optional)',
    checklistItems: 'Checklist Items',
    addItem: 'Add Item',
    snag: 'Snag',

    // ── Site Diary — missing keys ─────────────────────────────────────────────
    dailySiteDiary: 'Daily Site Diary',
    dailySiteDiarySubtitle: 'Record daily site activity, weather, labour and progress notes',
    createEntry: 'New Entry',
    viewEntries: 'View Entries',
    newDiaryEntry: 'New Diary Entry',
    newDiaryEntrySubtitle: 'Record today\'s site activity and progress',
    diaryEntries: 'Diary Entries',
    diaryEntriesSubtitle: 'All recorded daily site diary entries',
    selectWeather: 'Select weather…',
    sunny: 'Sunny',
    cloudy: 'Cloudy',
    rainy: 'Rainy',
    windy: 'Windy',
    overcast: 'Overcast',
    selectSiteRequired: 'Please select a site',
    selectDateRequired: 'Please select a date',
    selectWeatherRequired: 'Please select weather conditions',
    selectSitePlaceholder: 'Select a site…',
    weatherConditions: 'Weather Conditions',
    submittedBy: 'Submitted By',
    labourOnSite: 'Labour on Site',
    labourOnSitePlaceholder: 'List workers and trades on site today…',
    deliveries: 'Deliveries',
    deliveriesPlaceholder: 'Note any deliveries received today…',
    workCompleted: 'Work Completed',
    workCompletedPlaceholder: 'Describe the work completed today…',
    issuesDelays: 'Issues / Delays',
    issuesDelaysPlaceholder: 'Note any issues, delays or problems encountered…',
    generalNotes: 'General Notes',
    generalNotesPlaceholder: 'Any other notes about today\'s site activity…',
    dropPhotosOrClick: 'Drop photos here or click to browse',
    photoUploadFormats: 'JPG, PNG supported — up to 10 photos',
    saveDiaryEntry: 'Save Diary Entry',
    saving: 'Saving…',
    noAdditionalDetails: 'No additional details recorded.',
    filterBySite: 'Filter by Site',
    dateFrom: 'Date From',
    dateTo: 'Date To',
    clear: 'Clear',
    weather: 'Weather',
    details: 'Details',
    noDiaryEntriesFound: 'No diary entries found.',
    hide: 'Hide',

    // ── Compliance Report — missing keys ─────────────────────────────────────
    complianceReport: 'Compliance Report',
    complianceReportSubtitle: 'Generate and export a full compliance report for any site',
    exportAsPdf: 'Export as PDF',
    exportAsCsv: 'Export as CSV',
    exportCsv: 'Export CSV',
    selectSite: 'Select Site',
    chooseSiteToGenerateReport: 'Choose a site to generate the report…',
    errorLoadingReport: 'Failed to load report data',
    noSiteSelected: 'No site selected',
    selectSiteToGenerateReport: 'Select a site above to generate the compliance report.',

    // ── Auto-generated missing translations ──────────────────────────
    'actionNeeded': 'Action needed',
    'activeTasks': 'Active Tasks',
    'actual': 'Actual',
    'actualSpend': 'Actual Spend',
    'addComment': 'Add a comment',
    'addCommentPlaceholder': 'Write a comment...',
    'addCommentsOrCorrectiveActions': 'Add comments or corrective actions',
    'addTaskNotes': 'Add notes about this task...',
    'admin': 'Admin',
    'all': 'All',
    'allComplianceRequirementsMet': 'All compliance requirements met',
    'allRequirementsMet': 'All requirements met',
    'allTime': 'All time',
    'allValid': 'All valid',
    'allocatedBudget': 'Allocated Budget',
    'analytics': 'Analytics',
    'assignWorker': 'Assign Worker',
    'assignedSiteToday': 'Assigned site today',
    'assignedWorkers': 'Assigned Workers',
    'assignmentNote1': 'Workers will see their assignment when they log in.',
    'assignmentNote2': 'Site managers can manage workers on their assigned sites.',
    'assignmentNote3': 'Drag and drop workers onto days to assign them.',
    'assignmentNote4': 'Click an assignment to remove it.',
    'assignmentNotes': 'Assignment Notes',
    'attachment': 'Attachment',
    'attachments': 'Attachments',
    'attention': 'Attention',
    'auditDate': 'Audit Date',
    'auditName': 'Audit Name',
    'auditNamePlaceholder': 'Enter audit name',
    'auditQuestions': 'Audit Questions',
    'auditQuestionsDesc': 'Add questions for this audit',
    'auditTrail': 'Audit Trail',
    'auditTrailSubtitle': 'Complete history of site activity',
    'auditType': 'Audit Type',
    'auditorName': 'Auditor Name',
    'auditsInspectionsSubtitle': 'Manage site audits and inspections',
    'availableToSpend': 'Available to Spend',
    'backToDashboard': 'Back to Dashboard',
    'budget': 'Budget',
    'budgetAlert': 'Budget Alert',
    'budgetColActual': 'Actual',
    'budgetColBudget': 'Budget',
    'budgetCommitted': 'Committed',
    'budgetFullReport': 'Full Report',
    'budgetRemaining': 'Remaining',
    'budgetReport': 'Budget Report',
    'budgetReportSubtitle': 'Track project spend against budget',
    'budgetSpent': 'Spent',
    'budgetStatusOnTrack': 'On Track',
    'budgetStatusOver': 'Over Budget',
    'budgetTitle': 'Budget Overview',
    'budgetTotalBudget': 'Total Budget',
    'categoriesExceedingBudget': 'Categories exceeding budget',
    'categorySpendVsBudget': 'Category Spend vs Budget',
    'clickCardToDrillDown': 'Click a card to drill down',
    'clickRowToViewProfile': 'Click a row to view profile',
    'clickRowsToDrillDown': 'Click rows to drill down',
    'committed': 'Committed',
    'companyWideOverview': 'Company-wide Overview',
    'complete': 'Complete',
    'completeChecklist': 'Complete Checklist',
    'completeFirstChecklist': 'Complete your first checklist',
    'completeTask': 'Complete Task',
    'complianceSummary': 'Compliance Summary',
    'constructionCompliancePlatform': 'Construction Compliance Platform',
    'costCategory': 'Cost Category',
    'createAudit': 'Create Audit',
    'createFirstAudit': 'Create your first audit',
    'createNewAudit': 'Create New Audit',
    'createNewAuditSubtitle': 'Set up a new audit or inspection',
    'criticalAlert': 'Critical alert',
    'dailyChecklists': 'Daily Checklists',
    'days': 'days',
    'daysRemaining': 'days remaining',
    'demo.back_to_home': 'Back to Home',
    'demo.choose_role': 'Choose a role to explore',
    'demo.demo_user_label': 'Demo User',
    'demo.footer_link': 'Register your company',
    'demo.footer_note': 'Want your own account?',
    'demo.footer_note2': 'Register your company to get started.',
    'demo.live_demo_badge': 'Live Demo',
    'demo.register_company': 'Register your company',
    'demo.role.admin_badge': 'Full Access',
    'demo.role.admin_desc': 'Manage sites, workers, compliance, and billing.',
    'demo.role.admin_title': 'Admin',
    'demo.role.client_badge': 'View Only',
    'demo.role.client_desc': 'View project progress, photos, and reports.',
    'demo.role.client_title': 'Client',
    'demo.role.manager_badge': 'Site Access',
    'demo.role.manager_desc': 'Manage workers, tasks, and site compliance.',
    'demo.role.manager_title': 'Site Manager',
    'demo.role.worker_badge': 'Task Access',
    'demo.role.worker_desc': 'Complete tasks, upload photos, and report issues.',
    'demo.role.worker_title': 'Worker',
    'demo.sign_in_instead': 'Sign in instead',
    'demo.signing_in_as': 'Signing in as',
    'demo.subtitle': 'Try SiteProof with sample data',
    'demo.want_own_account': 'Want your own account?',
    'document': 'Document',
    'documentCompletion': 'Document Completion',
    'documentHasExpired': 'document has expired',
    'documentsCount': 'Documents',
    'documentsHaveExpired': 'documents have expired',
    'draft': 'Draft',
    'drawingPdf': 'Drawing PDF',
    'drawingPreview': 'Drawing Preview',
    'drawingViewer': 'Drawing Viewer',
    'enableIncidentReportingDesc': 'Allow workers and site managers to report incidents from the app',
    'enterAuditorName': 'Enter auditor name',
    'entries': 'Entries',
    'equipment': 'Equipment',
    'expiring': 'Expiring',
    'expiringSoon': 'Expiring soon',
    'fail': 'Fail',
    'filterAudits': 'Filter audits',
    'filterByCategory': 'Filter by category',
    'filterByStatus': 'Filter by status',
    'from': 'from',
    'fullLog': 'Full Log',
    'fullReport': 'Full Report',
    'getStartedCreateAudit': 'Get started by creating your first audit',
    'hasExceeded': 'has exceeded',
    'haveExceeded': 'have exceeded',
    'hideDetails': 'Hide details',
    'immediateActionRequired': 'Immediate action required',
    'inactive': 'Inactive',
    'includeModules': 'Include modules',
    'induction': 'Induction',
    'investigating': 'Investigating',
    'labour': 'Labour',
    'landing.create_account': 'Create Free Account',
    'landing.cta_h2': 'Ready to simplify compliance?',
    'landing.cta_subheading': 'Join construction companies across the UK using SiteProof.',
    'landing.dashboard_eyebrow': 'Dashboard',
    'landing.dashboard_h2': 'Everything in one place',
    'landing.dashboard_subheading': 'See site health, tasks, documents, and compliance at a glance.',
    'landing.features_eyebrow': 'Features',
    'landing.features_h2': 'Built for construction',
    'landing.hero_badge': 'UK Construction Compliance',
    'landing.hero_cta_demo': 'Try the Demo',
    'landing.hero_demo_note': 'No sign-up required',
    'landing.hero_h1': 'Construction compliance, simplified.',
    'landing.hero_subheading': 'One platform for documents, tasks, inspections, and site safety.',
    'landing.hero_trust_1': 'GDPR Compliant',
    'landing.hero_trust_2': 'UK Built',
    'landing.hero_trust_3': 'SSL Encrypted',
    'landing.hiw_eyebrow': 'How it works',
    'landing.hiw_h2': 'Get started in 3 steps',
    'landing.hiw_step1_desc': 'Create your account and add your company details.',
    'landing.hiw_step1_title': 'Register your company',
    'landing.hiw_step2_desc': 'Add your construction sites and invite your team.',
    'landing.hiw_step2_title': 'Set up your sites',
    'landing.hiw_step3_desc': 'Track documents, tasks, and inspections from one dashboard.',
    'landing.hiw_step3_title': 'Manage compliance',
    'landing.insp_export_pdf': 'Export PDF',
    'landing.insp_eyebrow': 'Inspections',
    'landing.insp_fail': 'Fail',
    'landing.insp_h2': 'Digital inspections',
    'landing.insp_item_1': 'Scaffolding secure',
    'landing.insp_item_2': 'PPE compliance',
    'landing.insp_item_3': 'Fire exits clear',
    'landing.insp_item_4': 'First aid kit stocked',
    'landing.insp_item_5': 'Welfare facilities clean',
    'landing.insp_na': 'N/A',
    'landing.insp_pass': 'Pass',
    'landing.insp_point_1': 'Custom checklists',
    'landing.insp_point_2': 'Photo evidence capture',
    'landing.insp_point_3': 'Instant PDF reports',
    'landing.insp_point_4': 'Automatic audit trail',
    'landing.insp_point_5': 'Mobile-friendly interface',
    'landing.insp_subheading': 'Run inspections on-site with your phone.',
    'landing.insp_summary': 'Inspection Summary',
    'landing.nav_features': 'Features',
    'landing.nav_login': 'Sign In',
    'landing.nav_pricing': 'Pricing',
    'landing.pillar_audit_trail': 'Audit Trail',
    'landing.pillar_checklists': 'Checklists',
    'landing.pillar_compliance_reporting': 'Compliance Reporting',
    'landing.pillar_compliance_reports': 'Compliance Reports',
    'landing.pillar_daily_tasks': 'Daily Tasks',
    'landing.pillar_drawings_plans': 'Drawings & Plans',
    'landing.pillar_expiry_alerts': 'Expiry Alerts',
    'landing.pillar_incident_reporting': 'Incident Reporting',
    'landing.pillar_inspections_audits': 'Inspections & Audits',
    'landing.pillar_pdf_csv': 'PDF & CSV Export',
    'landing.pillar_rams': 'H&S Docs',
    'landing.pillar_realtime_notifications': 'Real-time Notifications',
    'landing.pillar_reporting_oversight': 'Reporting & Oversight',
    'landing.pillar_role_access': 'Role-based Access',
    'landing.pillar_safety_compliance': 'Safety & Compliance',
    'landing.pillar_site_diary': 'Site Diary',
    'landing.pillar_site_inductions': 'Site Inductions',
    'landing.pillar_site_management': 'Site Management',
    'landing.pillar_snag_management': 'Snag Management',
    'landing.pillar_task_analytics': 'Task Analytics',
    'landing.pillar_team_invites': 'Team Invites',
    'landing.pillar_toolbox_talks': 'Toolbox Talks',
    'landing.pillar_worker_certs': 'Worker Certifications',
    'landing.pillar_worker_dashboard': 'Worker Dashboard',
    'landing.pillar_workforce_management': 'Workforce Management',
    'landing.pricing_eyebrow': 'Pricing',
    'landing.pricing_h2': 'Simple, transparent pricing',
    'landing.pricing_subheading': 'Pay per site. No hidden fees.',
    'landing.problem_cscs': 'CSCS cards expiring without notice',
    'landing.problem_h2': 'Sound familiar?',
    'landing.problem_incidents': 'Incidents reported on paper',
    'landing.problem_inspections': 'Inspections done on clipboard',
    'landing.problem_photos': 'Photo evidence scattered across phones',
    'landing.problem_rams': 'H&S docs buried in email chains',
    'landing.problem_tagline': 'SiteProof fixes all of this.',
    'landing.problem_toolbox': 'Toolbox talks with no sign-off',
    'login.confirming_email': 'Confirming your email...',
    'login.copyright': '© 2026 SiteProof. All rights reserved.',
    'login.demo_explore_desc': 'Try SiteProof with sample data — no sign-up required.',
    'login.demo_explore_title': 'Explore the Demo',
    'login.demo_footer': 'Want your own account?',
    'login.demo_footer_action': 'Register your company',
    'login.demo_footer_then': 'to get started.',
    'login.demo_signing_in': 'Signing in...',
    'login.demo_user_label': 'Demo User',
    'login.disclaimer': 'SiteProof is a compliance management platform for construction companies.',
    'login.email_invalid': 'Please enter a valid email address',
    'login.email_label': 'Email address',
    'login.email_not_verified_desc': 'Please check your inbox and verify your email before signing in.',
    'login.email_not_verified_title': 'Email not verified',
    'login.email_placeholder': 'Enter your email',
    'login.email_required': 'Email is required',
    'login.forgot_password': 'Forgot password?',
    'login.invalid_credentials': 'Invalid email or password. Please try again.',
    'login.no_account': 'Don\'t have an account?',
    'login.password_label': 'Password',
    'login.password_min': 'Password must be at least 8 characters',
    'login.password_placeholder': 'Enter your password',
    'login.password_required': 'Password is required',
    'login.register_company': 'Register your company',
    'login.remember_me': 'Remember me',
    'login.resend_error': 'Failed to resend. Please try again.',
    'login.resend_sending': 'Sending...',
    'login.resend_sent': 'Sent!',
    'login.resend_sent_msg': 'Verification email sent. Please check your inbox.',
    'login.resend_verification': 'Resend verification email',
    'login.reset_email_sent_desc': 'We\'ve sent a password reset link to your email.',
    'login.reset_email_sent_desc2': 'Check your spam folder if you don\'t see it.',
    'login.reset_email_sent_title': 'Check your email',
    'login.reset_password_desc': 'Enter your email and we\'ll send you a reset link.',
    'login.reset_password_title': 'Reset your password',
    'login.role_admin_desc': 'Full platform access and team management',
    'login.role_based_access': 'Role-based access',
    'login.role_client_desc': 'View project progress and reports',
    'login.role_manager_desc': 'Site oversight and worker management',
    'login.role_worker_desc': 'Daily tasks, photos, and compliance',
    'login.send_reset_link': 'Send reset link',
    'login.serving_uk': 'Proudly serving UK construction',
    'login.sign_in_btn': 'Sign In',
    'login.sign_in_heading': 'Sign in to SiteProof',
    'login.sign_in_subheading': 'Construction compliance, simplified.',
    'login.tagline': 'Construction Compliance Platform',
    'login.terms': 'By signing in, you agree to our Terms of Service and Privacy Policy.',
    'login.trust_gdpr': 'GDPR Compliant',
    'login.trust_gdpr_desc': 'Full compliance with UK data protection',
    'login.trust_ssl': 'SSL Encrypted',
    'login.trust_ssl_desc': 'Your data is encrypted end-to-end',
    'login.trust_uk': 'UK Built',
    'login.trust_uk_desc': 'Designed for UK construction regulations',
    'materials': 'Materials',
    'missingDocs': 'Missing Documents',
    'missingOrExpiredDocuments': 'Missing or expired documents',
    'myTasks': 'My Tasks',
    'newSite': 'New Site',
    'noAuditsFound': 'No audits found',
    'noAuditsYet': 'No audits yet',
    'noAuditsYetDesc': 'Create your first audit to start tracking compliance.',
    'noChecklistsYet': 'No checklists yet',
    'noChecklistsYetDesc': 'No checklists have been created for this site.',
    'noDocumentsInCategory': 'No documents in this category',
    'noDocumentsMatchFilters': 'No documents match the current filters',
    'noDrawingsYetDesc': 'Upload your first drawing to get started.',
    'noFileAvailable': 'No file available',
    'noIncidentsForSite': 'No incidents reported for this site',
    'noIncidentsReported': 'No incidents reported',
    'noIncidentsYet': 'No incidents yet',
    'noIncidentsYetDesc': 'No incidents have been reported.',
    'noMatchingAudits': 'No audits match the current filters',
    'noMatchingIncidents': 'No incidents match the current filters',
    'noPhotosAttached': 'No photos attached',
    'noProjectsMatchFilter': 'No projects match the current filter',
    'noSnagsLinked': 'No snags linked',
    'noWorkersFound': 'No workers found',
    'noWorkersMatchFilters': 'No workers match the current filters',
    'notCompleted': 'Not completed',
    'notesOptional': 'Notes (optional)',
    'of': 'of',
    'ofBudget': 'of budget',
    'openInNewTab': 'Open in new tab',
    'openSnags': 'Open Snags',
    'optional': 'Optional',
    'overBudget': 'Over Budget',
    'overBudgetLabel': 'Over budget',
    'overallBudgetUtilisation': 'Overall Budget Utilisation',
    'overallCompliance': 'Overall Compliance',
    'overallScore': 'Overall Score',
    'overdue': 'Overdue',
    'overdueTasks': 'Overdue Tasks',
    'overview': 'Overview',
    'pass': 'Pass',
    'pending': 'Pending',
    'photoAdded': 'Photo added',
    'photoUploaded': 'Photo uploaded',
    'photosUploaded': 'Photos uploaded',
    'plantCertificate': 'Plant Certificate',
    'postComment': 'Post comment',
    'posting': 'Posting...',
    'progress': 'Progress',
    'projectAllocation': 'Project Allocation',
    'projectRagStatus': 'Project RAG Status',
    'rate': 'Rate',
    'readOnly': 'Read only',
    'reg.already_have_account': 'Already have an account?',
    'reg.copyright': '© 2026 SiteProof. All rights reserved.',
    'reg.create_account': 'Create your account',
    'reg.subtitle': 'Start managing your construction compliance today.',
    'reg.terms': 'By registering, you agree to our Terms of Service and Privacy Policy.',
    'reg.time_estimate': 'Takes less than 2 minutes',
    'relatedSnags': 'Related Snags',
    'remaining': 'Remaining',
    'reportBuilderPanelSubtitle': 'Generate and export site reports',
    'reportFirstIncident': 'Report an incident',
    'reportGenerated': 'Report generated',
    'required': 'Required',
    'reset.back_to_signin': 'Back to sign in',
    'reset.confirm_mismatch': 'Passwords do not match',
    'reset.confirm_password_label': 'Confirm password',
    'reset.confirm_password_placeholder': 'Confirm new password',
    'reset.confirm_required': 'Please confirm your password',
    'reset.invalid_title': 'Invalid or expired link',
    'reset.new_password_label': 'New password',
    'reset.new_password_placeholder': 'Enter new password',
    'reset.pw_min': 'Password must be at least 8 characters',
    'reset.pw_required': 'Password is required',
    'reset.set_btn': 'Set password',
    'reset.set_new_password': 'Set new password',
    'reset.set_new_password_desc': 'Choose a strong password for your account.',
    'reset.sign_in_new': 'Sign in with new password',
    'reset.success_desc': 'Your password has been updated. You can now sign in.',
    'reset.success_title': 'Password updated',
    'reset.verifying': 'Verifying...',
    'resolvedBy': 'Resolved by',
    'result': 'Result',
    'reviewAndApprove': 'Review & Approve',
    'saveProgress': 'Save Progress',
    'searchAuditsPlaceholder': 'Search audits...',
    'searchIncidentsPlaceholder': 'Search incidents...',
    'selectSiteToView': 'Select a site to view',
    'showing': 'Showing',
    'siteAuditsInspections': 'Site Audits & Inspections',
    'siteHealthAiAnalysing': 'Analysing site data...',
    'siteHealthAiInsights': 'AI Insights',
    'siteHealthAiNoInsights': 'No insights available',
    'siteHealthAiPowered': 'AI-Powered',
    'siteHealthScoreLoading': 'Calculating site health...',
    'siteIncidents': 'Site Incidents',
    'siteName': 'Site Name',
    'siteNoAssigned': 'No sites assigned',
    'siteProject': 'Site / Project',
    'siteReference': 'Site Reference',
    'smStatusCompleted': 'Completed',
    'smStatusOverdue': 'Overdue',
    'smStatusPending': 'Pending',
    'snagPhoto': 'Snag Photo',
    'snagRaisedBy': 'Raised by',
    'someDocumentsExpiringSoon': 'Some documents are expiring soon',
    'sortBy': 'Sort by',
    'spent': 'Spent',
    'statusUnavailable': 'Status unavailable',
    'subcontractors': 'Subcontractors',
    'tapToAddPhoto': 'Tap to add photo',
    'taskMarkedAs': 'Task marked as',
    'taskOverview': 'Task Overview',
    'taskProgressOverdueBanner': '1 task is overdue',
    'taskProgressOverdueBannerPlural': 'tasks are overdue',
    'teamNoMembers': 'No team members',
    'teamNoMembersDesc': 'Invite team members to get started.',
    'thisQuarter': 'This quarter',
    'thisYear': 'This year',
    'to': 'to',
    'today': 'Today',
    'totalActualSpend': 'Total Actual Spend',
    'totalAudits': 'Total Audits',
    'totalBudget': 'Total Budget',
    'trackedDocuments': 'Tracked Documents',
    'trainingCertificate': 'Training Certificate',
    'underBudgetLabel': 'Under budget',
    'untitled': 'Untitled',
    'updateStatus': 'Update Status',
    'uploadFirstDrawing': 'Upload your first drawing',
    'uploadPhotoEvidence': 'Upload photo evidence',
    'validDocuments': 'Valid Documents',
    'variance': 'Variance',
    'varianceAnalysis': 'Variance Analysis',
    'varianceAnalysisSubtitle': 'Compare actual vs budgeted spend',
    'variationValue': 'Variation Value',
    'variations': 'Variations',
    'vars': 'Variations',
    'verified.cta': 'Continue to dashboard',
    'verified.heading': 'Email verified!',
    'verified.subtext': 'Your email has been verified. You can now sign in.',
    'verify.back_to_signin': 'Back to sign in',
    'verify.copyright': '© 2026 SiteProof. All rights reserved.',
    'verify.desc': 'We\'ve sent a verification link to your email.',
    'verify.desc2': 'Click the link to activate your account.',
    'verify.desc_no_email': 'Didn\'t receive an email?',
    'verify.footer_note': 'Having trouble? Contact support@site-proof.app',
    'verify.heading': 'Verify your email',
    'verify.resend_btn': 'Resend verification email',
    'verify.resend_sending': 'Sending...',
    'verify.resent_success': 'Verification email sent!',
    'verify.spam_bold1': 'Check your spam',
    'verify.spam_bold2': 'Check spam/junk',
    'verify.spam_notice': 'Can\'t find the email?',
    'verify.spam_notice2': 'or junk folder.',
    'viewComplianceReport': 'View Compliance Report',
    'viewInduction': 'View Induction',
    'viewSite': 'View Site',
    'welcomeBack': 'Welcome back',
    'witness': 'Witness',
    'workerAssignments': 'Worker Assignments',
    'workerAssignmentsSubtitle': 'Manage worker-to-site assignments',
    'workerInductions': 'Worker Inductions',
    'workerPerformance': 'Worker Performance',
    'last30Days': 'Last 30 days',
    'notYetScheduled': 'Not yet scheduled',
    'daysWithTasks': 'Days With Tasks',
    'daysCompleted': 'days completed',
    'noWorkersYet': 'No workers yet — invite workers to your company to see their completion rates.',

  },
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Always default to en-GB; clear any previously stored preference
    localStorage.removeItem('preferredLanguage');
    return 'en-GB';
  });

  const t = useCallback(
    (key) => {
      return (
        translations?.[language]?.[key] ??
        translations?.['en-GB']?.[key] ??
        key
      );
    },
    [language]
  );

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    localStorage.setItem('preferredLanguage', lang);
  }, []);

  const value = {
    language,
    setLanguage,
    t,
    languageOptions: LANGUAGE_OPTIONS,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;